import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SiteLayout from "../components/SiteLayout";
import { api } from "../lib/api";
import { CheckCircle2, Loader2, XCircle, FileSignature, CalendarDays } from "lucide-react";
import { format } from "date-fns";

function formatTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:${m.toString().padStart(2, "0")} ${ampm}`;
}

// Poll every 2s for up to 60s. Stripe's webhook usually lands within
// a couple seconds; the /payments/status endpoint also asks Stripe
// directly as a fallback so this loop always converges.
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 30;

export default function PaymentSuccess() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const sessionId = new URLSearchParams(search).get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | failed | error | timeout
  const [attempts, setAttempts] = useState(0);
  const timerRef = useRef(null);

  const [pending] = useState(() => {
    try {
      const raw = sessionStorage.getItem("gls_pending_booking");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    let n = 0;

    async function poll() {
      if (cancelled) return;
      n += 1;
      setAttempts(n);
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          try { sessionStorage.removeItem("gls_pending_booking"); } catch { /* ignore */ }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("failed");
          return;
        }
      } catch {
        // transient — keep polling until timeout
      }
      if (n >= POLL_MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    }
    poll();
    return () => { cancelled = true; if (timerRef.current) clearTimeout(timerRef.current); };
  }, [sessionId]);

  return (
    <SiteLayout>
      <section className="container-pad max-w-3xl mx-auto py-20">
        <div data-testid="payment-status-card" className="card-soft p-10 text-center">
          {state === "polling" && (
            <div data-testid="payment-polling">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-sage-500/15 text-sage-700 flex items-center justify-center">
                <Loader2 className="w-7 h-7 animate-spin" strokeWidth={1.75} />
              </div>
              <h1 className="mt-5 font-heading text-2xl font-semibold text-ink">Confirming your payment…</h1>
              <p className="mt-3 text-muted_text leading-relaxed max-w-md mx-auto">
                Please stay on this page. This usually takes just a few seconds.
              </p>
              <p className="mt-2 text-xs text-muted_text">Checked {attempts} of {POLL_MAX_ATTEMPTS} times</p>
            </div>
          )}

          {state === "paid" && (
            <div data-testid="payment-paid">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-sage-500/15 text-sage-700 flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7" strokeWidth={1.75} />
              </div>
              <h1 className="mt-5 font-heading text-2xl font-semibold text-ink">Payment confirmed. You&apos;re booked.</h1>
              <p className="mt-3 text-muted_text leading-relaxed max-w-md mx-auto">
                {pending?.full_name ? `Thank you, ${pending.full_name.split(" ")[0]}. ` : ""}
                {pending?.service && pending?.date && pending?.time ? (
                  <>Your <span className="font-medium text-ink">{pending.service}</span> for{" "}
                  <span className="font-medium text-ink">{format(new Date(pending.date), "EEEE, MMMM d")}</span> at{" "}
                  <span className="font-medium text-ink">{formatTime(pending.time)}</span> has been confirmed.</>
                ) : (
                  "Your appointment has been confirmed. A care coordinator will reach out shortly with the telehealth link."
                )}
              </p>

              <div data-testid="payment-intake-notice" className="mt-8 mx-auto max-w-xl rounded-2xl border border-gold-500/30 bg-gold-50 px-5 py-5 text-left">
                <p className="font-heading text-base font-semibold text-ink inline-flex items-center gap-2">
                  <FileSignature className="w-4 h-4 text-gold-700" strokeWidth={1.75} /> Required next step
                </p>
                <p className="mt-2 text-sm text-ink/80 leading-relaxed">
                  All patients must sign our <button
                    type="button"
                    data-testid="payment-open-consent-link"
                    onClick={() => pending?.appointment_id && navigate(`/consent?appointment_id=${pending.appointment_id}`)}
                    className="font-semibold text-sage-800 hover:text-gold-700 underline underline-offset-4 decoration-gold-500 decoration-2"
                  >HIPAA &amp; Telehealth consent form</button> before their first visit. It only takes a minute.
                </p>
              </div>

              <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                <button
                  data-testid="payment-open-consent-btn"
                  onClick={() => pending?.appointment_id && navigate(`/consent?appointment_id=${pending.appointment_id}`)}
                  disabled={!pending?.appointment_id}
                  className="btn-gold !text-sm disabled:opacity-60"
                >
                  <FileSignature className="w-4 h-4 mr-2" /> Continue to required consent
                </button>
                <Link to="/" data-testid="payment-back-home" className="btn-ghost">Back home</Link>
              </div>
            </div>
          )}

          {(state === "failed" || state === "error") && (
            <div data-testid="payment-failed">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-red-500/15 text-red-700 flex items-center justify-center">
                <XCircle className="w-7 h-7" strokeWidth={1.75} />
              </div>
              <h1 className="mt-5 font-heading text-2xl font-semibold text-ink">Payment didn&apos;t go through.</h1>
              <p className="mt-3 text-muted_text leading-relaxed max-w-md mx-auto">
                Your card wasn&apos;t charged. The time slot is still open &mdash; please try booking again.
              </p>
              <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                <Link to="/contact" data-testid="payment-retry" className="btn-gold !text-sm">
                  <CalendarDays className="w-4 h-4 mr-2" /> Try again
                </Link>
                <Link to="/" data-testid="payment-back-home" className="btn-ghost">Back home</Link>
              </div>
            </div>
          )}

          {state === "timeout" && (
            <div data-testid="payment-timeout">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-amber-500/15 text-amber-700 flex items-center justify-center">
                <Loader2 className="w-7 h-7" strokeWidth={1.75} />
              </div>
              <h1 className="mt-5 font-heading text-2xl font-semibold text-ink">Still processing…</h1>
              <p className="mt-3 text-muted_text leading-relaxed max-w-md mx-auto">
                Your payment is being verified. You&apos;ll receive a confirmation email once it&apos;s complete. If nothing arrives within 10 minutes, please <Link to="/contact" className="text-sage-700 underline">contact us</Link>.
              </p>
              <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                <Link to="/" data-testid="payment-back-home" className="btn-ghost">Back home</Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}
